import React, { useState } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import type { DpiaHistoryListItem } from 'config/api/dpiaRequests/dpiaRequests.types';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import { buildDpiaNeedAssessmentLocation } from 'pages/ProcessingActivities/DpiaNeedAssessmentPage/dpiaNeedAssessment.helpers';
import PATHS from 'router/PATHS';

import DpiaHistoryDialog from './DpiaHistoryDialog';
import useDpiaHistory from './useDpiaHistory';

export const resolveDpiaLogRecords = (items: DpiaHistoryListItem[], requestStatus?: string | null) => {
  const sorted = [...items].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
  const dpias = sorted.filter(item => item.kind === 'dpia');
  const assessments = sorted.filter(item => item.kind === 'dpia_request');
  const pending = requestStatus === 'draft' || requestStatus === 'indicated';
  const [latestAssessment] = assessments;
  const assessment = latestAssessment && (!pending || latestAssessment.status === requestStatus) ? latestAssessment : null;
  const latestDpia = dpias.find(item => item.isCurrent) ?? dpias[0];
  const dpia =
    !pending && latestDpia && (!assessment || Date.parse(latestDpia.createdAt) >= Date.parse(assessment.createdAt)) ? latestDpia : null;
  return {
    assessment,
    dpia,
    showHistory: dpias.length > 1 || assessments.length > 1 || (dpias.length > 0 && !dpia),
  };
};

type Props = { apaId: number; organizationId?: number; requestStatus?: string | null };

const DpiaLogRecordActions: React.FC<Props> = ({ apaId, organizationId, requestStatus }) => {
  const { t } = useTranslation();
  const navigation = useHistory();
  const [historyOpen, setHistoryOpen] = useState(false);
  const { history, isError, isUnavailable, isLoading, refetch } = useDpiaHistory({ apaId, organizationId });
  const messages = processing_activities_messages.asset_ropa.dpia_history;
  const records = resolveDpiaLogRecords(history?.items ?? [], requestStatus);
  const openRecord = (item: DpiaHistoryListItem) => {
    const returnPath = navigation.location.pathname;
    if (item.kind === 'dpia_request') {
      navigation.push(buildDpiaNeedAssessmentLocation({ apaId, requestId: item.id, returnPath }));
    } else if (item.kind === 'dpia') {
      navigation.push(generatePath(PATHS.PROCESSING_ACTIVITIES_ADMIN_DPIA_REVIEW, { dpiaId: String(item.id) }), { returnPath });
    }
  };

  if (isLoading) return <Typography variant='body2'>{t(general_messages.loading)}</Typography>;
  if (isError || isUnavailable) {
    return (
      <Box>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <Button onClick={() => refetch()}>{t(general_messages.retry)}</Button>
      </Box>
    );
  }

  return (
    <>
      <Box display='flex' flexWrap='wrap' gridGap={8} marginTop={1}>
        {records.dpia && (
          <Button onClick={() => openRecord(records.dpia as DpiaHistoryListItem)} variant='outlined'>
            {t(messages.open_dpia)}
          </Button>
        )}
        {records.assessment && (
          <Button onClick={() => openRecord(records.assessment as DpiaHistoryListItem)} variant='outlined'>
            {t(messages.open_assessment)}
          </Button>
        )}
        {records.showHistory && (
          <Button onClick={() => setHistoryOpen(true)} variant='outlined'>
            {t(processing_activities_messages.asset_ropa.owner_workspace.actions.view_dpia_history)}
          </Button>
        )}
      </Box>
      <DpiaHistoryDialog
        apaId={apaId}
        canOpenAssessments
        onClose={() => setHistoryOpen(false)}
        onOpenItem={openRecord}
        open={historyOpen}
        organizationId={organizationId}
      />
    </>
  );
};

export default DpiaLogRecordActions;
